import { useEffect } from 'react';

import { useForm, useWatch } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { useShallow } from 'zustand/react/shallow';

import { CountrySelect } from '@/entities/country';

import {
  Button,
  DashboardFormMessage,
  Form,
  FormControl,
  FormField,
  FormItem,
  Input,
} from '@/shared/ui';

import {
  SendMoneyRecipientStepData,
  sendMoneyRecipientSchema,
} from '../../../../model/schema_new';
import { useSendMoney } from '../../../../model/useSendMoney';

export const RecipientStepForm = () => {
  const { setStep, updateFormData, data } = useSendMoney(
    useShallow((state) => state),
  );

  const form = useForm<SendMoneyRecipientStepData>({
    defaultValues: {
      recipientAddress: {
        country: {},
        street: '',
        postcode: '',
        city: '',
      },
      email: '',
    },
    resolver: zodResolver(sendMoneyRecipientSchema),
    shouldFocusError: false,
    mode: 'onChange',
  });

  useEffect(() => {
    if (data?.recipientAddress) {
      form.setValue('recipientAddress', data.recipientAddress);
      form.setValue('recipientAddress.country', data.recipientAddress.country);
      form.setValue('email', data.email);
    }
  }, []);

  const formData = useWatch({
    control: form.control,
  });

  const onSubmit = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      updateFormData({
        ...formData,
      });
      setStep('amount');
    }
  };

  return (
    <div className="mt-5 flex-1">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex h-full w-full flex-col justify-between"
        >
          <div className="relative flex flex-col gap-y-4">
            <FormField
              control={form.control}
              name="recipientAddress.country"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CountrySelect
                      placeholder="Country"
                      value={field.value}
                      onSelect={field.onChange}
                      prefixQueryKey="send-recipient"
                      isInvalid={
                        !!form.formState.errors.recipientAddress?.country
                      }
                    />
                  </FormControl>
                  {!!form.formState.errors.recipientAddress?.country && (
                    <p className="mt-2 px-3 px-4 pb-2 pt-0.5 text-start text-[13px] text-negative-500 typography-12r">
                      Field is required
                    </p>
                  )}
                </FormItem>
              )}
            />
            <div className="flex flex-col gap-y-1">
              <FormField
                control={form.control}
                name="recipientAddress.street"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="w-full"
                        classNames={{ input: '!typography-16r' }}
                        id="recipient-address"
                        placeholder="Address"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <DashboardFormMessage>
                      {form.formState.errors.recipientAddress?.street?.message}
                    </DashboardFormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="recipientAddress.postcode"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="w-full"
                        classNames={{ input: '!typography-16r' }}
                        id="recipient-postcode"
                        placeholder="Postcode"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <DashboardFormMessage>
                      {
                        form.formState.errors.recipientAddress?.postcode
                          ?.message
                      }
                    </DashboardFormMessage>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="recipientAddress.city"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="w-full"
                        classNames={{ input: '!typography-16r' }}
                        id="recipient-city"
                        placeholder="City"
                        type="text"
                        {...field}
                      />
                    </FormControl>
                    <DashboardFormMessage>
                      {form.formState.errors.recipientAddress?.city?.message}
                    </DashboardFormMessage>
                  </FormItem>
                )}
              />
            </div>
            <div className="mb-8 flex flex-col gap-y-1">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="w-full"
                        classNames={{ input: '!typography-16r' }}
                        id="recipient-email"
                        placeholder="E-mail"
                        type="text"
                        description="Optional"
                        {...field}
                      />
                    </FormControl>
                    <DashboardFormMessage>
                      {form.formState.errors.email?.message}
                    </DashboardFormMessage>
                  </FormItem>
                )}
              />
            </div>
          </div>


        </form>
      </Form>
      <Button
        className="w-full sticky bottom-1"
        size="lg"
        type="submit"
        variant="brand-500"
        onClick={onSubmit}
      >
        Continue
      </Button>
    </div>
  );
};
